import React, { useState } from 'react';
import styles from './TrainerRow.module.scss';
import classNames from 'classnames';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import { getFullName } from '../../utils/dashboardUtils';
import ConfirmationDialog from '../DialogWrappers/ConfirmationDialog';
import Menu from '../atoms/Menu';

import type { Trainer } from '../../types';

interface TrainerRowProps {
  trainer: Trainer;
  selected: boolean;
  onSelect?: () => void;
  deleteTrainer: (trainer: Trainer) => Promise<void>;
}

const TrainerRow: React.FC<TrainerRowProps> = ({
  trainer,
  onSelect,
  selected,
  deleteTrainer,
}) => {
  const [isDeleteTrainerConfirmationOpen, setIsDeleteTrainerConfirmationOpen] =
    useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleCloseMenuOptions = () => {
    setAnchorEl(null);
  };

  const renderTrainerName = () => {
    const trainerName = getFullName(trainer);
    return (
      <div className={classNames(styles.trainerColumn, styles.trainerName)}>
        {trainerName}
      </div>
    );
  };

  const renderClients = () => {
    return (
      <div className={styles.trainerColumn}>
        {`${trainer.memberIds.length} Clients`}
      </div>
    );
  };

  const handleDeleteTrainer = async () => {
    handleCloseMenuOptions();
    await deleteTrainer(trainer);
    setIsDeleteTrainerConfirmationOpen(false);
  };

  const renderMoreOptions = () => {
    const menuOptions = [
      {
        onClick: () => {
          handleCloseMenuOptions();
          setIsDeleteTrainerConfirmationOpen(true);
        },
        label: 'Delete Trainer',
        icon: <DeleteForeverOutlinedIcon color="primary" />,
      },
    ];
    return (
      <div className={classNames(styles.trainerColumn, styles.options)}>
        <PendingOutlinedIcon
          className={styles.optionsBtn}
          color="primary"
          onClick={(event) => {
            event.stopPropagation();
            setAnchorEl(event.currentTarget as unknown as HTMLElement);
          }}
        />
        <Menu
          anchorEl={anchorEl}
          isOpen={open}
          onClose={handleCloseMenuOptions}
          options={menuOptions}
        />
      </div>
    );
  };

  const renderDeleteTrainerConfirmationDialog = () => (
    <ConfirmationDialog
      text="Are you sure you want to delete this trainer?"
      onSubmit={handleDeleteTrainer}
      submitText="Yes"
      cancelText="No"
      isOpen={isDeleteTrainerConfirmationOpen}
      onCancel={() => setIsDeleteTrainerConfirmationOpen(false)}
      symbol={<DeleteForeverOutlinedIcon />}
    />
  );

  const handleRowClick = () => {
    // Don't trigger row selection if menu is open
    // Let the menu close naturally when clicking away
    if (!open && onSelect) {
      onSelect();
    }
  };

  return (
    <>
      <div
        onClick={handleRowClick}
        className={classNames(styles.trainerRow, styles.listGrid, {
          [styles.selected]: selected,
        })}
      >
        {renderTrainerName()}
        {renderClients()}
        {renderMoreOptions()}
      </div>
      {renderDeleteTrainerConfirmationDialog()}
    </>
  );
};

export default TrainerRow;
